import type { TimeSeriesItem } from '../../../../hooks/useCompanyTimeSeries';
import {
  getYearArrayFromTimeseries,
  getEndDateByYear,
} from '../../../ExtendedIncomeStatement/components/Sankey/utils/utils';
import { getFreeCashFlowDifference } from '../FreeCashFlowChart/utils';
import type { CurrencyISO } from '@/constants/currencies';
import { toCurrency } from '@/utilities/formatting';
import { usePastTranslation } from '../../../../hooks/usePastTranslation';
import { useCallback } from 'react';
import { bisector } from 'd3-array';
import { format } from 'date-fns';

const FIRST_COLUMN_HEADER = '';

export const getTableHeaders = (timeSeries?: TimeSeriesItem[]) => {
  if (!timeSeries) {
    return [];
  }
  const years = getYearArrayFromTimeseries(timeSeries);
  const yearHeaders = years.map((year) => year.toString());
  return [FIRST_COLUMN_HEADER, ...yearHeaders];
};

type GetTableBodyRowParams = {
  timeSeries: TimeSeriesItem[];
  rowKey: string;
  firstRowName: string;
};

const normaliseTimeSeriesForFCF = (timeseriesItem: TimeSeriesItem) => {
  const {
    netIncome,
    depreciationAmortizationTotal,
    stockBasedCompensation,
    changeNetWorkingCapital,
    leveredFreeCashFlow,
  } = timeseriesItem;
  const netWorkingCapital = changeNetWorkingCapital
    ? -changeNetWorkingCapital
    : null;

  const others = getFreeCashFlowDifference(
    netIncome,
    depreciationAmortizationTotal,
    stockBasedCompensation || 0,
    netWorkingCapital,
    leveredFreeCashFlow
  );

  return {
    ...timeseriesItem,
    changeNetWorkingCapital: netWorkingCapital,
    others,
  };
};
const getTimeSeriesItem = (timeSeries: TimeSeriesItem[], endDate: string) => {
  const normalizedEndDate = new Date(endDate);
  return timeSeries.find((item) => {
    const itemDate = new Date(item.date);
    return itemDate.toDateString() === normalizedEndDate.toDateString();
  });
};

export const pickTimeSeriesData = (
  timeSeries: TimeSeriesItem[],
  endDate?: string
) => {
  const normalizedEndDate = endDate || format(new Date(), 'yyyy-MM-dd');
  const endDateItem = getTimeSeriesItem(timeSeries, normalizedEndDate);

  if (endDateItem) {
    return endDateItem;
  }

  const bisect = bisector((d: TimeSeriesItem) => new Date(d.date));
  const index =
    bisect.left(timeSeries, new Date(normalizedEndDate), 1, timeSeries.length) -
    1; // zero-based index
  return timeSeries[index];
};

const getTableBodyRow = ({
  timeSeries,
  rowKey,
  firstRowName,
}: GetTableBodyRowParams) => {
  const years = getYearArrayFromTimeseries(timeSeries);
  const endDates = years.map((year) => getEndDateByYear(year));

  const yearValuesByKey = endDates.map((endDate) => {
    if (!timeSeries?.length) {
      return '';
    }
    const chosenTimeseries = pickTimeSeriesData(timeSeries, endDate);
    const data = normaliseTimeSeriesForFCF(chosenTimeseries);
    return data[rowKey] || '';
  });

  return [firstRowName, ...yearValuesByKey];
};

const TABLE_ROWS = [
  'netIncome',
  'depreciationAmortizationTotal',
  'stockBasedCompensation',
  'changeNetWorkingCapital',
  'others',
  'leveredFreeCashFlow',
] as const;

export const useTableBody = () => {
  const t = usePastTranslation();
  return useCallback(
    (timeSeries?: TimeSeriesItem[]) => {
      if (!timeSeries) {
        return [];
      }

      const fixedRows = TABLE_ROWS.map((rowKey) => {
        const firstRowName = t(`freeCashFlow.table.${rowKey}`);
        return getTableBodyRow({
          timeSeries,
          rowKey,
          firstRowName,
        });
      });

      const fixedRowsWithoutEmpty = fixedRows.filter((row) => row.length > 0);
      return fixedRowsWithoutEmpty;
    },
    [t]
  );
};

export const formatCurrencyForCellData = (
  value: string | number,
  currencyISO: CurrencyISO
) => {
  const cellDataInNumber = Number(value);
  if (Number.isNaN(cellDataInNumber)) {
    return value;
  }
  return toCurrency(cellDataInNumber, 2, currencyISO);
};
